import { useState } from 'react'
import Typography from '@mui/material/Typography'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import Tab from '@mui/material/Tab'

import { useTranslation } from 'react-i18next'

import {
  getOption,
  useAbstractSourceOptions,
  useAbstractTypeOptions,
  useDomainsDataOptions,
  useThemesDataOptions,
  useKeywordsDataOptions,
} from '../../../utils'
import { Abstracts, AbstractSource, UserLanguages } from '../../../types'
import Dropzone from '../../root/components/Dropzone'
import Heading from '../../root/components/Heading'

interface Props {
  abstract: Abstracts | undefined
}

const tabsStyle = { height: '1rem', margin: '0.1rem', minHeight: '1rem' }
const tabPanelsStyle = { padding: 0 }

export default function AbstractDetails({ abstract }: Props) {
  const { t } = useTranslation('abstracts')
  const [locale, setLocale] = useState<UserLanguages>('fr')

  const typesOptions = useAbstractTypeOptions(t)
  const sourcesOptions = useAbstractSourceOptions(t)
  const domainsOptions = useDomainsDataOptions()
  const themesOptions = useThemesDataOptions()
  const keywordsOptions = useKeywordsDataOptions()

  const handleLocale = (_event: React.SyntheticEvent, newLocale: UserLanguages) => {
    setLocale(newLocale)
  }

  if (!abstract) {
    return null
  }

  return (
    <Stack gap="1rem">
      <Heading>
        <Typography variant="h3">{t('Informations')}</Typography>
      </Heading>

      <Autocomplete
        value={getOption(typesOptions, null, abstract.type)}
        options={typesOptions}
        disablePortal
        fullWidth
        disabled
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label={t('Type de résumé')}
            slotProps={{
              input: {
                disabled: true,
              },
            }}
          />
        )}
      />
      <Autocomplete
        value={getOption(sourcesOptions, null, abstract.source)}
        options={sourcesOptions}
        disablePortal
        fullWidth
        disabled
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label={t('Source')}
            slotProps={{
              input: {
                disabled: true,
              },
            }}
          />
        )}
      />
      <TextField
        label={t('Autre source')}
        fullWidth
        variant="outlined"
        sx={{ display: abstract.source === AbstractSource.Other ? undefined : 'none' }}
        disabled
        slotProps={{
          input: { disabled: true },
        }}
      />
      <Autocomplete
        value={getOption(domainsOptions, null, abstract.domain)}
        options={domainsOptions}
        disablePortal
        fullWidth
        disabled
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label={t('Domaine')}
            slotProps={{
              input: {
                disabled: true,
              },
            }}
          />
        )}
      />
      <Autocomplete
        value={getOption(themesOptions, null, abstract.theme)}
        options={themesOptions}
        disablePortal
        fullWidth
        disabled
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label={t('Thème et sous-thème')}
            slotProps={{
              input: {
                disabled: true,
              },
            }}
          />
        )}
      />
      <Autocomplete
        value={abstract.keywords?.map(val => getOption(keywordsOptions, null, val))}
        options={keywordsOptions}
        multiple
        disablePortal
        fullWidth
        disabled
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label={t('Mots-clés')}
            slotProps={{
              input: {
                disabled: true,
              },
            }}
          />
        )}
      />

      <TabContext value={locale}>
        <Heading>
          <Typography variant="h3">{t('Résumé à évaluer')}</Typography>
          <TabList
            onChange={handleLocale}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ minHeight: 'auto' }}
          >
            <Tab label="FR" value="fr" sx={tabsStyle} />
            <Tab label="EN" value="en" sx={tabsStyle} />
          </TabList>
        </Heading>

        <TabPanel value="fr" sx={tabPanelsStyle}>
          <Stack direction="column" spacing={2}>
            <TextField
              label={t('Titre', { lng: 'fr' })}
              value={abstract.title}
              fullWidth
              // disabled
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Objectif', { lng: 'fr' })}
              value={abstract.objective}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Matériaux et méthodes', { lng: 'fr' })}
              value={abstract.materials_and_methods}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Résultats', { lng: 'fr' })}
              value={abstract.results}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Conclusion', { lng: 'fr' })}
              value={abstract.conclusions}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
          </Stack>
        </TabPanel>

        <TabPanel value="en" sx={tabPanelsStyle}>
          <Stack direction="column" spacing={2}>
            <TextField
              label={t('Titre', { lng: 'en' })}
              value={abstract.title_en}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Objectif', { lng: 'en' })}
              value={abstract.objective_en}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Matériaux et méthodes', { lng: 'en' })}
              value={abstract.materials_and_methods_en}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Résultats', { lng: 'en' })}
              value={abstract.results_en}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
            <TextField
              label={t('Conclusion', { lng: 'en' })}
              value={abstract.conclusions_en}
              fullWidth
              variant="outlined"
              multiline
              slotProps={{
                input: {
                  disabled: true,
                },
              }}
            />
          </Stack>
        </TabPanel>
      </TabContext>

      {abstract.figures.length > 0 && (
        <>
          <Heading>
            <Typography variant="h3">{t('Figures')}</Typography>
          </Heading>
          <Dropzone defaultValue={abstract.figures} disabled />
        </>
      )}
    </Stack>
  )
}
